import React, { useState } from "react";
import {
    StyledRadioContainer,
    StyledInput,
    StyledLabel,
    StyledIconContainer,
} from "./RadioStyles";
import { Color } from "../../theme/theme.model";

export interface RadioProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    /**
     * Specifies the initial state: whether or not the radio is selected
     * @default false
     */
    defaultChecked?: boolean;
    /**
     * Radio label, that come on the right | left side
     */
    label?: string;
    /**
     * Color on checked. Choose one of the theme color
     * @default primary
     */
    color?: Color;
    /**
     * If *id* is empty default will be a random number
     */
    id?: string;
    /**
     * Title position left or right of radio
     * @default right
     */
    align?: "left" | "right";
    /**
     * Variant to change component view
     * @default default
     */
    variant?: "default" | "small" | "medium";
    /**
     * css className
     */
    classNames?: string;
    /**
     * Attributes applied to the input element.
     */
    inputProps?: any;
    /**
     * The icon name.
     */
    icon?: React.ReactNode;
    /**
     * The checked icon name.
     */
    checkedIcon?: React.ReactNode;
}

const Radio = (props: RadioProps) => {
    const {
        onChange,
        defaultChecked = false,
        align = "right",
        classNames = "",
        color = "primary",
        variant = "default",
        id,
        name,
        disabled,
        inputProps,
        icon,
        checkedIcon,
        ...otherProps
    } = props;

    const [radioState, setRadioState] = useState(defaultChecked);

    /**
     * Send event, status and value of the radio on change
     */
    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRadioState(event.target.checked);

        if (typeof onChange !== "undefined") {
            onChange(event);
        }
        return event;
    };

    // set random number if id not exist
    const inputId =
        typeof id !== "undefined" ? id : Math.floor(Math.random() * 1000);

    return (
        <StyledRadioContainer
            icon={icon}
            variant={variant}
            className={`h-radio ${classNames}`}
            color={color}
        >
            <StyledInput
                id={`el-${inputId}`}
                type="radio"
                name={name}
                disabled={disabled}
                value={otherProps?.value}
                defaultChecked={radioState}
                onChange={handleOnChange}
                {...inputProps}
            />
            <StyledLabel htmlFor={`el-${inputId}`}>
                {icon && (
                    <StyledIconContainer
                        variant={variant}
                        className="icon-checked"
                    >
                        {checkedIcon}
                        {icon}
                    </StyledIconContainer>
                )}

                {align === "left" && <span>{otherProps?.label}</span>}

                {!icon && <span className="checked"></span>}

                {align === "right" && <span>{otherProps?.label}</span>}
            </StyledLabel>
        </StyledRadioContainer>
    );
};

export default Radio;
